<template>
  <div class="ml-20 mt-5 flex items-center gap-2">
    <IconArrowLeft2></IconArrowLeft2>
    <router-link to="/">{{ $t('action.goBack') }}</router-link>
  </div>

  <div class="h-full mx-auto w-[600px] flex flex-col mt-5 p-10 border-solid border rounded-xl">
    <div class="font-bold text-center text-2xl pb-5 border-b">
      {{ $t('page.adminChatTitle') }}
    </div>

    <div class="flex w-full flex-1">
      <div class="flex w-full py-5 overflow-y-auto flex-col gap-5 mb-40 overflow-y-scroll">
        <template v-for="msg in chatData" :key="msg.id || `${msg.date_created}-${msg.text}`">
          <div v-if="!isAdminMessage(msg)" class="self-end flex flex-col gap-2 items-end w-full">
            <div class="text-black">{{ userLabel }}</div>

            <div
              v-if="msg.text"
              class="w-max max-w-[80%] rounded-lg px-5 py-2.5 bg-accent-color text-white break-words"
            >
              {{ msg.text }}
            </div>

            <div
              v-for="att in msg.attachments || []"
              :key="att.id"
              class="w-max max-w-[80%] flex gap-3 rounded-lg px-5 py-2.5 bg-accent-color text-white"
            >
              <IconDocument class="w-6 h-6 shrink-0"></IconDocument>
              <p class="break-all">{{ getAttachmentName(att.path) }}</p>
              <button @click="downloadFile(att.path)">
                <IconDownload></IconDownload>
              </button>
            </div>

            <div class="text-[#797C7B] text-xs">
              {{ msg.date_created }}
            </div>
          </div>

          <div v-else class="self-start flex flex-col gap-2 items-start w-full">
            <div class="text-black">{{ adminLabel }}</div>

            <div
              v-if="msg.text"
              class="w-max max-w-[80%] rounded-lg px-5 py-2.5 bg-green-500 text-white break-words"
            >
              {{ msg.text }}
            </div>

            <div
              v-for="att in msg.attachments || []"
              :key="att.id"
              class="w-max max-w-[80%] flex gap-3 rounded-lg px-5 py-2.5 bg-green-500 text-white"
            >
              <IconDocument class="w-6 h-6 shrink-0"></IconDocument>
              <p class="break-all">{{ getAttachmentName(att.path) }}</p>
              <button @click="downloadFile(att.path)">
                <IconDownload></IconDownload>
              </button>
            </div>

            <div class="text-[#797C7B] text-xs">
              {{ msg.date_created }}
            </div>
          </div>
        </template>
      </div>
    </div>

    <div
      v-if="files.length"
      class="fixed bottom-[81px] left-0 px-10 z-30 w-full py-3 bg-[#f2f2f2] border-t border-gp-grey-40 no-print"
    >
      <div class="grid grid-cols-3 gap-5 mt-4 max-lg:grid-cols-2 max-md:grid-cols-1">
        <div
          v-for="file in files"
          :key="file.localId"
          class="w-full flex items-start p-2 gap-2 border border-solid rounded"
        >
          <IconDocument class="w-10 h-10 shrink-0"></IconDocument>

          <div class="flex-1 min-w-0">
            <label class="break-all">
              {{ file.detail.name }}
            </label>

            <div class="mt-1">
              <div class="text-xs" :class="getFileStatusClass(file)">
                {{ getFileStatusText(file) }}
              </div>

              <div
                v-if="file.isUploading"
                class="mt-1 h-1.5 w-full rounded bg-[#D9D9D9] overflow-hidden"
              >
                <div
                  class="h-full bg-main-color transition-all duration-200"
                  :style="{ width: `${file.progress || 0}%` }"
                ></div>
              </div>
            </div>
          </div>

          <button
            class="ml-3 mt-1 bg-[#EB5757] p-2.5 rounded disabled:opacity-50 disabled:cursor-not-allowed"
            type="button"
            :disabled="file.isUploading"
            @click="remove(file.localId)"
            title="Remove file"
          >
            <IconTrash></IconTrash>
          </button>
        </div>
      </div>
    </div>

    <div
      class="fixed bottom-0 left-0 px-10 z-30 w-full py-3 bg-[#f2f2f2] border-t border-gp-grey-40 no-print"
    >
      <div class="flex items-center justify-center gap-2">
        <input
          v-on:keyup.enter="sendMessage"
          v-model="chatContent.text"
          type="text"
          :placeholder="placeholderText"
          class="bg-white w-full px-4 py-4 rounded-xl outline-none"
        />

        <label
          for="fileUploadClient"
          class="bg-main-color rounded-full h-10 w-10 flex justify-center items-center active:bg-main-color hover:bg-accent-color ease-linear transition-all duration-150"
        >
          <IconClip class="[&>path]:!stroke-white"></IconClip>
          <input
            ref="file"
            name="files"
            class="hidden"
            type="file"
            multiple
            id="fileUploadClient"
            @change="onFileChange"
          />
        </label>

        <button
          @click="sendMessage"
          :disabled="hasUploadingFiles"
          class="bg-main-color rounded-full h-10 w-10 flex justify-center items-center active:bg-main-color hover:bg-accent-color ease-linear transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <IconSend></IconSend>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/api';
import IconSend from '@icons/IconSend.vue';
import IconArrowLeft2 from '@icons/IconArrowLeft2.vue';
import IconClip from '@icons/IconClip.vue';
import IconDocument from '@icons/IconDocument.vue';
import IconTrash from '@icons/IconTrash.vue';
import IconDownload from '@icons/IconDownload.vue';

export default {
  name: 'clientChat',
  components: {
    IconSend,
    IconArrowLeft2,
    IconClip,
    IconDocument,
    IconTrash,
    IconDownload,
  },
  data() {
    return {
      chatData: [],
      chatContent: {
        text: '',
      },
      loading: false,
      files: [],
      fileUrls: [],
      fileIds: [],
      polling: null,
    };
  },
  computed: {
    currentLocale() {
      const queryLang = this.$route?.query?.lang;
      if (typeof queryLang === 'string' && queryLang.trim()) {
        return queryLang.trim().toLowerCase();
      }

      const locale = this.$i18n?.locale;
      if (typeof locale === 'string') return locale.toLowerCase();
      if (locale && typeof locale.value === 'string') return locale.value.toLowerCase();

      return 'ru';
    },
    isRussianLocale() {
      return this.currentLocale.startsWith('ru');
    },
    adminLabel() {
      return this.isRussianLocale ? 'Админ' : 'Admin';
    },
    userLabel() {
      return this.isRussianLocale ? 'Пользователь' : 'Ulanyjy';
    },
    placeholderText() {
      return this.isRussianLocale ? 'написать' : 'ýaz';
    },
    hasUploadingFiles() {
      return this.files.some(file => file.isUploading);
    },
  },

  methods: {
    notifyUnreadCount(count) {
      window.dispatchEvent(
        new CustomEvent('admin-chat-unread-updated', {
          detail: { count },
        }),
      );
    },
    isAdminMessage(msg) {
      const value = msg?.is_from_admin;

      if (value === true || value === 1 || value === '1') return true;
      if (value === false || value === 0 || value === '0' || value == null) return false;

      if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        if (['true', 'yes', '1'].includes(normalized)) return true;
        if (['false', 'no', '0', 'null', 'none', ''].includes(normalized)) return false;
      }

      return Boolean(value);
    },
    getAttachmentName(path) {
      if (!path) return '';
      const parts = path.split('/');
      return parts[parts.length - 1];
    },
    downloadFile(fileUrl) {
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = fileUrl.split('/').pop();
      link.click();
    },
    getFileStatusText(file) {
      if (file.isError) {
        return this.isRussianLocale ? 'Ошибка загрузки' : 'Ýüklemek ýalňyşdy';
      }

      if (file.isUploaded) {
        return this.isRussianLocale ? 'Загружено' : 'Ýüklendi';
      }

      return this.isRussianLocale
        ? `Загрузка... ${file.progress || 0}%`
        : `Ýüklenýär... ${file.progress || 0}%`;
    },
    getFileStatusClass(file) {
      if (file.isError) return 'text-red-500';
      if (file.isUploaded) return 'text-green-600';
      return 'text-[#797C7B]';
    },
    cleanupFilePreview(file) {
      if (file?.path && typeof file.path === 'string' && file.path.startsWith('blob:')) {
        URL.revokeObjectURL(file.path);
      }
    },
    cleanupAllFilePreviews() {
      this.files.forEach(file => this.cleanupFilePreview(file));
    },

    updateFile(localId, patch) {
      const index = this.files.findIndex(file => file.localId === localId);
      if (index === -1) return;

      this.files.splice(index, 1, {
        ...this.files[index],
        ...patch,
      });
    },

    remove(localId) {
      const index = this.files.findIndex(file => file.localId === localId);
      if (index === -1) return;

      const file = this.files[index];
      if (file?.uploadedId) {
        this.fileIds = this.fileIds.filter(id => id !== file.uploadedId);
      }

      this.cleanupFilePreview(file);
      this.files.splice(index, 1);
    },
    async onFileChange() {
      const input = this.$refs.file;
      if (!input?.files?.length) return;

      const selectedFiles = Array.from(input.files);
      input.value = '';
      this.loading = true;

      try {
        for (const f of selectedFiles) {
          const localId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;

          this.files.push({
            localId,
            detail: f,
            path: URL.createObjectURL(f),
            isUploading: true,
            isUploaded: false,
            isError: false,
            progress: 0,
            uploadedId: null,
          });

          const fileForm = new FormData();
          fileForm.append('path', f);

          try {
            const response = await api.post('files/medias/', fileForm, {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
              onUploadProgress: progressEvent => {
                if (!progressEvent?.total) return;

                this.updateFile(localId, {
                  progress: Math.min(
                    100,
                    Math.round((progressEvent.loaded * 100) / progressEvent.total),
                  ),
                });
              },
            });

            if (response && response.status === 201) {
              this.updateFile(localId, {
                progress: 100,
                isUploading: false,
                isUploaded: true,
                isError: false,
                uploadedId: response.data.id,
              });

              this.fileIds.push(response.data.id);
            } else {
              this.updateFile(localId, {
                isUploading: false,
                isUploaded: false,
                isError: true,
                progress: 0,
              });
            }
          } catch (e) {
            this.updateFile(localId, {
              isUploading: false,
              isUploaded: false,
              isError: true,
              progress: 0,
            });
            console.log(e);
          }
        }
      } finally {
        this.loading = false;
      }
    },

    async markAdminMessagesAsRead() {
      try {
        await api.get('message_to_admin/', {
          params: { mark_as_read: 1 },
          skipToast: true,
        });

        this.notifyUnreadCount(0);
      } catch (e) {
        console.log(e);
      }
    },
    async sendMessage() {
      if (this.hasUploadingFiles) return;
      if (!this.chatContent.text && this.fileIds.length === 0) return;

      this.loading = true;

      try {
        const body = {};

        if (this.chatContent.text !== '') {
          body.text = this.chatContent.text;
        }

        if (this.fileIds.length > 0) {
          body.attachments = this.fileIds;
        }

        await api.post('message_to_admin/', body);

        this.chatContent.text = '';
        this.cleanupAllFilePreviews();
        this.files = [];
        this.fileUrls = [];
        this.fileIds = [];

        await this.receiveMessage({ markAsRead: true });
      } catch (e) {
        console.log(e);
      }

      this.loading = false;
    },
    async receiveMessage({ markAsRead = false } = {}) {
      this.loading = true;

      try {
        const response = await api.get('message_to_admin/', {
          skipToast: true,
        });
        this.chatData = (response?.data?.results || []).reverse();

        if (markAsRead) {
          await this.markAdminMessagesAsRead();
        }
      } catch (e) {
        console.log(e);
      }

      this.loading = false;
    },
    pollData() {
      this.polling = setInterval(() => {
        this.receiveMessage({ markAsRead: true });
      }, 15000);
    },
  },
  mounted() {
    this.pollData();
  },
  beforeUnmount() {
    clearInterval(this.polling);
    this.cleanupAllFilePreviews();
  },
  async created() {
    await this.receiveMessage({ markAsRead: true });
  },
};
</script>
